import { WorkspaceBrandingProvider, WorkspaceIdentity, useWorkspaceBranding } from "@/lib/workspaceBranding";
import { FieldWorkModeProvider, FieldWorkModeSwitch, useFieldWorkMode } from "@/lib/fieldWorkMode";
import SecureWorkspaceEntry from "@/components/SecureWorkspaceEntry";
import { BillingLifecycleProvider } from "@/lib/billingLifecycle";
import { SubscriptionExpiryBanner } from "@/components/SubscriptionExpiryBanner";
import { useTradeFieldPresentation } from '@/lib/tradeFieldPresentation';
import { usePoll } from "@/lib/usePoll";
import { FieldBottom, FieldMore } from '@/components/trades/field/FieldNavigation';
import { useNavigationPresentation, navigationGroups } from "@/lib/tradeNavigation";
import "@/styles/fieldworker.css";
import "./v5/v5Shell.css";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import PlatformShell from "@/components/platform/PlatformShell";
import { Link, NavLink, Outlet, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { GlobalBackButton } from "@/components/GlobalBackButton";
import {
  HardHat, LayoutDashboard, Clock, ShieldAlert, Hammer, Users, Truck,
  CalendarDays, UserCog, LogOut, Menu, X, Inbox as InboxIcon, Banknote,
  ClipboardCheck, Wrench, MessageSquare, LayoutGrid, ShieldCheck, CreditCard, User, FileCheck2,
  Calendar, ClipboardList, Package, LifeBuoy, Boxes, ChevronDown, Palette, Receipt, FileSignature, FileText, Library, Briefcase, Sparkles, MapPin, HelpCircle, FolderKanban, Trophy, Heart, Phone, Search, Zap, PanelLeftClose, PanelLeftOpen, Sun, Moon, BarChart3, Megaphone, Link2, TrendingUp, Bell, Settings, Home as HomeIcon, Fuel, ShoppingCart, Plus, TriangleAlert, ArrowUpRight, PencilRuler, Building2,
} from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { BroadcastBanner } from "@/components/BroadcastBanner";
import { AppVersionTag } from "@/components/AppUpdateBanner";
import { BiometricNudge } from "@/components/BiometricNudge";
import { InstallPrompt } from "@/components/InstallPrompt";
import { CommandPalette } from "@/components/CommandPalette";
import { QuickAdd } from "@/components/QuickAdd";
import { NotificationsBell } from "@/components/NotificationsBell";
import { EmergencyAlert, EmergencyTrigger } from "@/components/Emergency";
import { AiHelper } from "@/components/AiHelper";
import { Kiri } from "@/components/Kiri";
import { AnnouncementBlocker } from "@/components/AnnouncementBlocker";
import { SubscriptionLockGate } from "@/components/SubscriptionLockGate";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { OfflineBanner } from "@/components/OfflineBanner";
import { useAuth } from "@/lib/auth";
import { ReviewPromptModal } from "@/components/ReviewPromptModal";
import { XeroReconnectPrompt } from "@/components/XeroReconnectPrompt";
import HaveYourSay from "@/components/HaveYourSay";
import { useTheme } from "@/lib/theme";
import { useTodayWeather, WX_TEXT } from "@/lib/useWeather";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

// Navigation model lives in @/constants/nav — shared with breadcrumbs.
import { GROUP_RAIL_ICONS, NAV_GROUPS, workspaceForPath } from "@/constants/nav";
import { WorkspaceSwitcher, rememberWorkspaceRoute } from "@/components/WorkspaceSwitcher";
import { ChatButton, HelpMenu, ProfileMenu, Breadcrumbs, WhatsNewBadge } from "@/components/TopNav";

// Bottom navigation for field workers, with quick actions in the centre.
// Pre-Start + SSSPs + Forms launched from the Safety hub tile row.
// Yard / Materials moved to a Home-page tile to free this slot for Jobs.
const FIELD_BOTTOM = [
  { to: "/app", label: "Today", icon: HomeIcon, end: true },
  { to: "/app/timesheets", label: "Timesheets", icon: Clock },
  { to: "/app/jobs", label: "Jobs", icon: Briefcase },
  { to: "/app/inbox", label: "Alerts", icon: Bell },
];

export default function DashboardLayout() {
  const { user } = useAuth();
  return <BillingLifecycleProvider key={`${user?.id}:${user?.org_id}`}><WorkspaceBrandingProvider><SecureWorkspaceEntry><FieldWorkModeProvider><DashboardShell /></FieldWorkModeProvider></SecureWorkspaceEntry></WorkspaceBrandingProvider></BillingLifecycleProvider>;
}

// These routes encode tabs in the pathname but retain the same page context.
function scrollPageKey(pathname) {
  const path = pathname.replace(/\/+$/, "") || "/";
  const hub = path.match(/^\/app\/(safety-qa|invoicing|team-hub|time|plant)(?:\/[^/]+)?$/);
  if (hub) return `/app/${hub[1]}`;
  const job = path.match(/^(\/app\/jobs\/[^/]+)(?:\/([^/]+))?$/);
  if (job && job[2] !== "charge-up") return job[1];
  return path;
}

function DashboardShell() {
  const branding = useWorkspaceBranding();
  const workMode = useFieldWorkMode();
  const truckingMode = workMode.dual && workMode.mode === "trucking";
  const fieldBottom = truckingMode ? [{to:"/app/my-runs",label:"My Run",icon:Truck},{to:"/app/trucking-time",label:"My Time",icon:Clock},{to:"/app/prestarts",label:"Pre-start",icon:Wrench},{to:"/app/inbox",label:"Alerts",icon:Bell}] : FIELD_BOTTOM;
  const { user, logout } = useAuth();
  const presentation = useNavigationPresentation();
  const fieldPresentation = useTradeFieldPresentation();
  const { theme, setTheme, resolved } = useTheme();
  const isDark = resolved === "dark";
  const nav = useNavigate();
  const loc = useLocation();
  const navigationType = useNavigationType();
  const pageKey = scrollPageKey(loc.pathname);
  const previousPageKey = useRef(pageKey);
  useLayoutEffect(() => {
    const pageChanged = previousPageKey.current !== pageKey;
    previousPageKey.current = pageKey;
    // The shell grows with its content; the document is the primary scroller.
    // Leave browser history restoration and same-page UI state alone.
    if (pageChanged && navigationType !== "POP") {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
  }, [pageKey, navigationType]);
  useEffect(() => { if (!presentation.trades && !presentation.pending && loc.pathname !== "/app/platform") rememberWorkspaceRoute(workspaceForPath(loc.pathname), loc.pathname + (loc.search || "")); }, [loc.pathname, loc.search, presentation.trades, presentation.pending]);
  const [open, setOpen] = useState(false);
  const [fieldNavOpen, setFieldNavOpen] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  // "What's new" pill — true when the user hasn't visited the changelog
  // since the latest release.
  const [hasUnseenChange, setHasUnseenChange] = useState(false);
  // Unread "What's new" count — stored per user on the server so it follows them across devices.
  useEffect(() => {
    const refresh = () => api.get("/changelog", { params: { summary: true } }).then(({ data }) => {
      setHasUnseenChange((data.unseen_count || 0) > 0);
      setUnseenCount(data.unseen_count || 0);
    }).catch(() => {});
    refresh();
    const clear = () => { setHasUnseenChange(false); setUnseenCount(0); };
    window.addEventListener("fl-whats-new-seen", clear);
    return () => window.removeEventListener("fl-whats-new-seen", clear);
  }, []);
  const [unseenCount, setUnseenCount] = useState(0);
  // Per-group collapsed state, persisted to localStorage.
  // Keep the active group open; remember explicit choices for other groups.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem("fl_nav_collapsed");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const toggleGroup = (id) => {
    setCollapsed((prev) => {
      const next = { ...prev, [id]: !(prev[id] ?? true) };
      try { localStorage.setItem("fl_nav_collapsed", JSON.stringify(next)); } catch { /* localStorage disabled */ }
      return next;
    });
  };

  // Desktop cockpit rail expand/collapse — persisted per-user so it sticks.
  const [railExpanded, setRailExpanded] = useState(() => {
    try {
      const raw = localStorage.getItem("fl_rail_expanded");
      return raw === null ? true : raw === "1";
    } catch {
      return true;
    }
  });
  const toggleRail = () => {
    setRailExpanded((prev) => {
      const next = !prev;
      try { localStorage.setItem("fl_rail_expanded", next ? "1" : "0"); } catch { /* localStorage disabled */ }
      return next;
    });
  };

  const chatUnreadOn = !!user && user.role !== "super_admin";
  const fetchChatUnread = useCallback(() => {
    api.get("/chat/unread").then((r) => setChatUnread(r.data.total || 0)).catch(() => {});
  }, []);
  usePoll(fetchChatUnread, 20000, { hiddenMs: 60000, enabled: chatUnreadOn });
  // Clear the badge as soon as chat is read, without waiting for the next poll.
  useEffect(() => {
    if (!chatUnreadOn || !chatUnread) return undefined;
    window.addEventListener("fl:chat-read", fetchChatUnread);
    return () => window.removeEventListener("fl:chat-read", fetchChatUnread);
  }, [chatUnreadOn, chatUnread, fetchChatUnread]);

  // PWA app-icon badge (the "dot" on the phone's home screen icon)
  useEffect(() => {
    try {
      if (chatUnread > 0 && navigator.setAppBadge) navigator.setAppBadge(chatUnread);
      else if (navigator.clearAppBadge) navigator.clearAppBadge();
    } catch { /* Badging API unavailable */ }
  }, [chatUnread]);

  // Global Cmd/Ctrl+K listener for the command palette (office only).
  useEffect(() => {
    if (!user || user.role === "field" || loc.pathname === "/app/platform") return;
    const onKey = (e) => {
      const isInput = ["INPUT", "TEXTAREA"].includes(e.target?.tagName) || e.target?.isContentEditable;
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      } else if (e.key === "/" && !isInput && !paletteOpen) {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [user, paletteOpen, loc.pathname]);

  // First time an office user lands on the app and hasn't
  // picked a theme yet, opt them into dark mode — Direction 2 Construction
  // Field workers follow the device theme unless they choose a preference.
  useEffect(() => {
    if (!user || user.role === "field") return;
    if (theme !== "auto") return;
    try {
      const stored = localStorage.getItem("fl-theme");
      // ThemeProvider persists "auto" on first load — only a real light/dark
      // pick counts as the user having chosen.
      if (stored && stored !== "auto") return;
    } catch { /* ignore */ }
    setTheme("dark");
  }, [user, theme, setTheme]);

  // Mark NEW-badged nav routes as visited so their badge disappears.
  useEffect(() => {
    NAV_GROUPS.forEach((g) => g.items.forEach((i) => {
      if (i.badgeNew && (loc.pathname === i.to || loc.pathname.startsWith(i.to + "/"))) {
        try { localStorage.setItem(`fl_seen_new:${i.to}`, "1"); } catch { /* ignore */ }
      }
    }));
  }, [loc.pathname]);

  if (!user) return null;
  if (user.role === "super_admin" && loc.pathname === "/app/platform") return <PlatformShell user={user} isDark={isDark} onTheme={() => setTheme(isDark ? "light" : "dark")} onLogout={() => { logout(); nav("/login"); }}><Outlet /></PlatformShell>;
  if (presentation.pending || fieldPresentation.pending) return <div className="p-8" role="status">Loading organisation navigation… <button className="underline" onClick={presentation.refresh}>Retry</button></div>;
  const isField = user.role === "field";
  const v5Preview = !isField && loc.pathname.replace(/\/$/, "") === "/app";
  const v5Shell = !isField;

  // Build visible groups from the shared nav model (roles + flags preserved).
  const workspace = workspaceForPath(loc.pathname);
  const visibleGroups = navigationGroups(user, workspace, presentation.trades);

  const routeMatch = (entry) => { const i = presentation.trades ? { ...entry, to: entry.to.split("?")[0] } : entry; return (i.end ? loc.pathname === i.to : loc.pathname === i.to || loc.pathname.startsWith(i.to + "/")); };
  // Auto-expand a collapsed group if the current route lives inside it.
  const activeGroupId = visibleGroups.find((g) =>
    g.items.some((i) => (i.children ? i.children.some(routeMatch) : routeMatch(i)))
  )?.id;

  // "NEW" feature badges — hidden once visited, and auto-expire after 45 days.
  const isNewItem = (i) => {
    if (!i.badgeNew) return false;
    try {
      if (localStorage.getItem(`fl_seen_new:${i.to}`)) return false;
    } catch { /* ignore */ }
    return (Date.now() - new Date(i.badgeNew).getTime()) < 45 * 86400000;
  };

  const onLogout = async () => {
    await logout();
    toast.success("You’ve been signed out.", { position: "bottom-right", duration: 3000 });
    nav("/login");
  };

  return (
    <div className={`min-h-screen bg-[#F7F8FA] dark:bg-[#07090D] text-zinc-900 dark:text-zinc-100 flex flex-col app-shell ${isField ? "field-app" : ""} ${v5Shell ? "v5-shell" : ""} ${v5Preview ? "v5-preview-route" : ""}`} style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <ImpersonationBanner user={user} />
      <OfflineBanner />
      <div className="flex flex-1 min-h-0">      {/* ============================================================
            OFFICE SIDEBAR
            Mobile (<lg): full 256px slide-in drawer with labels.
            Desktop (lg+): 64px icon rail. Group entries open a popover
            listing the group's items; top "home" group items (Overview /
            Inbox / Chat) are individual rail entries.
            ============================================================ */}
      {!isField && (
        <>
          {/* Mobile drawer (lg:hidden) — keep the existing dense layout */}
          <aside
            className={`${open ? "translate-x-0" : "-translate-x-full"} fl-nav-surface fl-nav-theme lg:hidden fixed z-40 inset-y-0 left-0 w-64 bg-[#07111A] fl-nav-primary text-white border-r fl-nav-border border-white/[0.07] transition-transform flex flex-col`}
            style={{ paddingTop: "env(safe-area-inset-top, 0)" }}
          >
            <div className="p-4 border-b fl-nav-border border-white/[0.07] flex items-center justify-between gap-3">
              <Link to="/app" className="flex items-center gap-2.5 min-w-0 flex-1">
                <WorkspaceIdentity />
              </Link>
              <button onClick={() => setOpen(false)} className="fl-nav-muted text-[#7B8798] hover:text-white shrink-0" aria-label="Close menu">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-3 pt-3">
              <WorkspaceSwitcher current={workspace} user={user} expanded variant="mobile" onNavigate={() => setOpen(false)} />
            </div>
            <nav className="flex-1 p-3 space-y-1 overflow-y-auto fl-sidebar" data-testid="sidebar-nav-mobile">
              {visibleGroups.map((g, gi) => {
                const hasLabel = !!g.label;
                const isCollapsed = hasLabel && (collapsed[g.id] ?? true) && g.id !== activeGroupId;
                return (
                  <div key={g.id} className={gi > 0 ? "pt-3" : ""}>
                    {hasLabel && (
                      <button
                        type="button"
                        onClick={() => toggleGroup(g.id)}
                        className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] fl-nav-muted text-[#7B8798] hover:text-[#C6CEDA] transition-colors"
                        data-testid={`nav-group-toggle-${g.id}`}
                        aria-expanded={!isCollapsed}>
                        <span>{g.label}</span>
                        <ChevronDown
                          className={`w-3 h-3 transition-transform duration-200 ${isCollapsed ? "-rotate-90" : "rotate-0"}`}
                          strokeWidth={2.5}
                        />
                      </button>
                    )}
                    {!isCollapsed && (
                      <div className="space-y-1 mt-1" data-testid={`nav-group-${g.id}`}>
                        {g.items.flatMap((i) => (i.children ? i.children : [i])).map((i) => {
                          const Icon = i.icon;
                          return (
                            <NavLink
                              key={i.to}
                              to={i.to}
                              end={i.end}
                              data-testid={i.testId ? `${i.testId}-m` : undefined}
                              onClick={() => setOpen(false)}
                              className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 text-[14px] font-semibold rounded-lg transition-colors duration-150 ${
                                  isActive ? ROW_ACTIVE : ROW_IDLE
                                }`
                              }>
                              <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
                              <span>{i.label}</span>
                              {i.showUnread && chatUnread > 0 && (
                                <span className="ml-auto bg-[#FF5F15] fl-nav-primary text-white text-[11px] font-bold px-1.5 py-0.5 rounded-full" data-testid="nav-chat-unread-m">
                                  {chatUnread}
                                </span>
                              )}
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
            <div className="p-4 border-t fl-nav-border border-white/[0.07]">
              <Button onClick={onLogout} variant="outline" size="sm" className="w-full rounded-lg bg-transparent fl-nav-border border-white/15 fl-nav-primary text-[#C6CEDA] hover:bg-white/[0.06] hover:text-white" data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </Button>
            </div>
          </aside>

          {/* Desktop branded dark sidebar (lg+) — same in light and dark themes */}
          <aside
            className={`fl-nav-surface fl-nav-theme hidden lg:flex shrink-0 flex-col bg-[#07111A] border-r fl-nav-border border-white/[0.07] fl-nav-primary text-white transition-[width] duration-200 lg:sticky lg:top-0 lg:h-screen ${railExpanded ? "w-[232px] items-stretch" : "w-16 items-center"}`}
            data-testid="cockpit-rail"
            data-expanded={railExpanded ? "true" : "false"}
          >
            {/* logo */}
            <div className={`border-b fl-nav-border border-white/[0.07] ${railExpanded ? "px-5 py-4" : "w-full py-3 px-1.5"}`}>
              <Link to="/app" className="block" title={branding.displayName}>
                <WorkspaceIdentity compact={!railExpanded} />
              </Link>
            </div>

            {/* Workspace switcher — primary nav control */}
            <div className={`border-b fl-nav-border border-white/[0.07] ${railExpanded ? "px-2.5 py-2.5" : "w-full py-2.5 px-1.5"}`}>
              <WorkspaceSwitcher current={workspace} user={user} expanded={railExpanded} variant="desktop" />
            </div>

            <div className={`flex-1 w-full overflow-y-auto fl-sidebar py-2.5 flex flex-col gap-0.5 ${railExpanded ? "px-2.5 items-stretch" : "items-center"}`}>
              {visibleGroups.map((g) => {
                // "home" group has no label → render its items as individual rail entries.
                if (!g.label) {
                  return g.items.map((i) => (
                    <RailLink key={i.to} to={i.to} end={i.end} icon={i.icon} label={i.label}
                      testId={i.testId} badge={0} expanded={railExpanded} isNew={isNewItem(i)} />
                  ));
                }
                // Collapsed rail → one icon per group, flyout popover with items.
                if (!railExpanded) {
                  return (
                    <RailGroup key={g.id} id={g.id} icon={GROUP_RAIL_ICONS[g.id] || LayoutGrid}
                      label={g.label} items={g.items} active={g.id === activeGroupId} isNewItem={isNewItem} />
                  );
                }
                // Expanded rail → collapsible labelled section with items/submenus.
                const isGroupCollapsed = (collapsed[g.id] ?? true) && g.id !== activeGroupId;
                return (
                  <div key={g.id} className="w-full" data-testid={`rail-group-${g.id}`}>
                    <button type="button" onClick={() => toggleGroup(g.id)} aria-expanded={!isGroupCollapsed}
                      className="w-full mt-3.5 mb-1 px-2 flex items-center gap-2 group/hd h-[26px]"
                      data-testid={`nav-group-toggle-${g.id}-d`}>
                      <span className="text-[11px] font-bold tracking-[0.18em] uppercase fl-nav-muted text-[#7B8798] group-hover/hd:text-[#C6CEDA] whitespace-nowrap transition-colors duration-150">{g.label}</span>
                      <span className="flex-1 h-px fl-nav-divider bg-white/[0.07]" />
                      <ChevronDown className={`w-3 h-3 fl-nav-muted text-[#66717F] transition-transform duration-150 ${isGroupCollapsed ? "-rotate-90" : ""}`} strokeWidth={2.5} />
                    </button>
                    {!isGroupCollapsed && g.items.map((i) => (
                      <SideItem key={i.to || i.id} item={i} pathname={loc.pathname} isNew={isNewItem(i)} />
                    ))}
                  </div>
                );
              })}
            </div>

            {railExpanded && <AppVersionTag className="mb-1 fl-nav-muted text-[#66717F]" />}
            <button
              type="button"
              onClick={toggleRail}
              className={`border-t fl-nav-border border-white/[0.07] hover:bg-white/[0.05] py-3 fl-nav-muted text-[#7B8798] hover:text-white transition-colors duration-150 flex items-center ${railExpanded ? "px-5 gap-2 text-[12.5px] font-semibold justify-start" : "w-full justify-center"}`}
              data-testid="rail-toggle"
              title={railExpanded ? "Collapse sidebar" : "Expand sidebar"}
              aria-label={railExpanded ? "Collapse sidebar" : "Expand sidebar"}
            >
              {railExpanded ? <PanelLeftClose className="w-4 h-4" /> : <PanelLeftOpen className="w-4 h-4" />}
              {railExpanded && "Collapse"}
            </button>
          </aside>

        </>
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Sticky top — orange notch-fill (iOS safe area) + white header */}
        <div className="sticky top-0 z-30">
          {/* iPhone notch / Dynamic Island fill. Renders zero-height on devices
              without a notch (Android, desktop, browser tabs), so it's invisible
              there but stops the Dynamic Island sitting on top of the header. */}
          <div className="bg-white dark:bg-[#FF5F15]" style={{ height: "env(safe-area-inset-top, 0px)" }} aria-hidden="true" data-testid="ios-notch-fill" />
          <header className="fl-topbar-surface bg-card text-zinc-900 border-b border-zinc-200 dark:text-white dark:border-white/[0.07] ">
          <div className="px-4 lg:px-5 min-h-[60px] py-2 flex items-center gap-2 lg:gap-3">
            {isField ? (
              <div className="flex items-center gap-2.5 min-w-0">
                <button
                  onClick={() => setFieldNavOpen(true)}
                  className="w-10 h-10 shrink-0 rounded-xl border border-zinc-200 dark:border-white/10 flex items-center justify-center text-zinc-900 dark:text-white active:scale-95 transition-transform"
                  aria-label="Open menu"
                  data-testid="field-menu-button"
                >
                  <Menu className="w-5 h-5" />
                </button>
                <Link to="/app" className="flex items-center gap-2 min-w-0" data-testid="field-header-logo">
                  <WorkspaceIdentity />
                </Link>
              </div>
            ) : (
              <button onClick={() => setOpen(true)} className="lg:hidden text-zinc-900 dark:text-white" aria-label="Open menu" data-testid="open-sidebar">
                <Menu className="w-6 h-6" />
              </button>
            )}
            {!isField && (
              <button
                type="button"
                onClick={() => setPaletteOpen(true)}
                data-testid="header-search-trigger"
                className="hidden md:inline-flex items-center gap-2.5 h-9 pl-3 pr-2 rounded-lg bg-zinc-100 hover:bg-zinc-200 border border-zinc-200 text-zinc-600 hover:text-zinc-900 dark:bg-[#121316] dark:hover:bg-[#101620] dark:border-[#1F2937] dark:text-zinc-400 dark:hover:text-white transition-colors duration-150 text-[12.5px] flex-1 max-w-[460px]"
                aria-label="Open search"
                title="Search (Cmd+K)"
              >
                <Search className="w-4 h-4 shrink-0" />
                <span className="flex-1 min-w-0 text-left truncate">Search workspace…</span>
                <kbd className="font-mono text-[11px] font-bold uppercase tracking-widest border border-zinc-300 dark:border-zinc-700 rounded-sm px-1.5 py-0.5">
                  ⌘K
                </kbd>
              </button>
            )}
            <div className="flex-1" />
            <div className="flex items-center gap-1.5 lg:gap-2">
              {!isField && <QuickAdd role={user.role} onViewAll={() => setPaletteOpen(true)} />}
              {!isField && <EmergencyTrigger />}
              {!isField && user.role !== "super_admin" && <ChatButton unread={chatUnread} />}
              {!isField && <NotificationsBell />}
              {!isField && (user.role === "admin" || user.role === "office") && <HaveYourSay />}
              {!isField && <WhatsNewBadge count={unseenCount} />}
              {!isField && <HelpMenu role={user.role} hasUnseenChange={hasUnseenChange} unseenCount={unseenCount} />}
              {!isField && <div className="hidden sm:block w-px h-6 bg-zinc-200 dark:bg-white/10 mx-0.5" aria-hidden="true" />}
              {!isField && <ProfileMenu user={user} onLogout={onLogout} showRole={v5Shell} />}
              {isField && (
                <div className="flex items-center gap-1">
                  <Link
                    to="/app/profile"
                    className="active:scale-95 transition-transform mr-1"
                    aria-label="Open profile"
                    data-testid="field-header-avatar"
                  >
                    <Avatar className="w-9 h-9 rounded-full ring-1 ring-zinc-200 dark:ring-white/20">
                      {user.avatar_path ? (
                        <AvatarImage src={`${api.defaults.baseURL}/files/view?path=${encodeURIComponent(user.avatar_path)}&auth=${encodeURIComponent(localStorage.getItem("fl_token") || "")}`} alt={user.name} />
                      ) : null}
                      <AvatarFallback className="bg-[#FF5F15] text-white text-xs font-bold rounded-full">
                        {user.name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Link>
                  <Link to="/app/chat" className="relative w-10 h-10 flex items-center justify-center text-zinc-800 dark:text-white hover:text-[#FF5F15] transition-colors" data-testid="field-header-chat" aria-label="Messages">
                    <MessageSquare className="w-[22px] h-[22px]" strokeWidth={1.9} />
                    {chatUnread > 0 && (
                      <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#FF5F15] text-white text-[11px] font-bold leading-none flex items-center justify-center ring-2 ring-white dark:ring-[#101620]" data-testid="field-chat-dot">
                        {chatUnread > 99 ? "99+" : chatUnread}
                      </span>
                    )}
                  </Link>
                </div>
              )}
            </div>
          </div>
          </header>
          <SubscriptionExpiryBanner />
          {isField && <GlobalBackButton />}
          {!isField && <Breadcrumbs leading={<GlobalBackButton compact />} hideTrail={v5Preview} />}
        </div>

        <BroadcastBanner />
        <BiometricNudge user={user} />
        <AnnouncementBlocker />
        <EmergencyAlert />
        {/* Legacy AI helper hidden (user request 2026-08-23) — superseded by Kiri below */}
        {false && (user.role === "admin" || user.role === "office" || user.role === "super_admin") && <AiHelper />}
        <Kiri />

        {/* Body — phone-first container width cap for field workers (pages own their padding) */}
        <main className={`flex-1 ${isField ? "pb-safe bg-[#F6F7F9] dark:bg-[#0B1018]" : ""}`}>
          <SubscriptionLockGate>
          {isField ? (
            <div className={`field-content w-full ${/^\/app\/plant\/fleet\/[^/]+\/?$/.test(loc.pathname) ? "" : "max-w-2xl mx-auto"} pb-24 dark:text-zinc-100 min-h-screen`} data-testid="field-container">
              {fieldPresentation.trades && !truckingMode && <details className="px-4"><summary className="min-h-11 flex items-center cursor-pointer text-sm text-muted-foreground">Switch work mode</summary><FieldWorkModeSwitch /></details>}
              {user.landfill_operator === true && loc.pathname === "/app" && (
                <Link to="/app/landfill" data-testid="field-landfill-cta"
                  className="mx-4 mt-3 flex items-center gap-3 rounded-2xl border border-[#FF5F15]/40 bg-[#FF5F15]/10 p-4 active:scale-[0.99] transition-transform">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#FF5F15] text-white"><Truck className="h-6 w-6" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-base font-bold leading-tight">Landfill Operations</span>
                    <span className="block text-sm text-zinc-600 dark:text-zinc-300">Record incoming loads</span>
                  </span>
                  <ArrowUpRight className="h-5 w-5 text-[#FF5F15]" />
                </Link>
              )}
              <Outlet key={fieldPresentation.trades ? fieldPresentation.identity : undefined} />
            </div>
          ) : (
            <Outlet />
          )}
          </SubscriptionLockGate>
        </main>
        <ReviewPromptModal />
        <XeroReconnectPrompt />

        {/* Field bottom nav — mockup style: 2 items · Quick Add FAB · 2 items */}
        {isField && fieldPresentation.trades && !truckingMode && <FieldBottom onMore={() => setFieldNavOpen(true)} />}
        {isField && (!fieldPresentation.trades || truckingMode) && (
          <>
            <Sheet open={quickAddOpen} onOpenChange={setQuickAddOpen}>
              <SheetContent side="bottom" className="field-quick-sheet rounded-t-2xl max-w-2xl mx-auto pb-8" onCloseAutoFocus={event => { event.preventDefault(); document.querySelector('[data-testid="field-quick-add-fab"]')?.focus(); }}>
                <SheetTitle>Quick actions</SheetTitle>
                <SheetDescription>What would you like to record?</SheetDescription>
                <div className="grid grid-cols-2 gap-3 mt-5" data-testid="quick-add-sheet">
                    {([
                      ...(user.landfill_operator ? [{ to: "/app/landfill", label: "Landfill load", icon: Truck }] : []),
                      ...(truckingMode ? [{to:"/app/my-runs",label:"Open run / add load",icon:Truck},{to:"/app/prestarts",label:"Pre-start",icon:Wrench},{to:"/app/fuel",label:"Fuel log",icon:Fuel}] : [
                      { to: "/app/crew-needs", label: "Crew needs", icon: ClipboardList },
                      { to: "/app/timesheets?new=1", label: "Timesheet", icon: Clock },
                      { to: "/app/prestarts", label: "Pre-start", icon: Wrench },
                      { to: "/app/toolbox", label: "Toolbox", icon: ClipboardCheck },
                      { to: "/app/safety", label: "Hazard", icon: ShieldAlert },
                      { to: "/app/fuel", label: "Fuel log", icon: Fuel },
                    ])]).map((q) => (
                      <Link key={q.label} to={q.to} onClick={() => setQuickAddOpen(false)}
                            className={`flex items-center gap-3 min-h-14 p-4 rounded-xl bg-zinc-50 dark:bg-[#151C27] hover:bg-zinc-100 dark:hover:bg-[#26272C] ${q.label === "Timesheet" ? "col-span-2" : ""}`}
                            data-testid={`quick-add-${q.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                        <q.icon className="w-5 h-5 text-[#FF5F15]" />
                        <span className="text-sm font-semibold whitespace-nowrap">{q.label}</span>
                      </Link>
                    ))}
                </div>
              </SheetContent>
            </Sheet>
            <nav
              className="fixed bottom-0 inset-x-0 z-40 px-3"
              aria-label="Main navigation"
              data-testid="field-bottom-nav"
              style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px), 10px)" }}
            >
              <div className="max-w-md mx-auto rounded-[24px] bg-white/90 dark:bg-[#101620]/90  border border-zinc-200 dark:border-[#1F2937] shadow-2xl shadow-black/30 grid grid-cols-5 items-end px-1">
                {fieldBottom.slice(0, 2).map((i) => <FieldNavItem key={i.to} item={i} />)}
                <div className="relative flex flex-col items-center justify-end pb-1.5">
                  <button
                    type="button"
                    onClick={() => setQuickAddOpen((v) => !v)}
                    className={`-mt-6 w-14 h-14 rounded-full bg-[#FF5F15] hover:bg-[#E65100] text-white flex items-center justify-center shadow-lg shadow-[#FF5F15]/40 active:scale-95 transition-all ${quickAddOpen ? "rotate-45" : ""}`}
                    data-testid="field-quick-add-fab" data-brand-primary="true"
                    aria-label={quickAddOpen ? "Close quick actions" : "Open quick actions"}
                    aria-expanded={quickAddOpen}
                    style={{ transitionProperty: "transform, background-color" }}
                  >
                    <Plus className="w-7 h-7" strokeWidth={2.4} />
                  </button>
                  <span className="text-[11px] mt-0.5 font-medium text-zinc-500 dark:text-zinc-400">Quick Add</span>
                </div>
                {fieldBottom.slice(2).map((i) => <FieldNavItem key={i.to} item={i} />)}
              </div>
            </nav>
          </>
        )}
      </div>
      {!isField && (
        <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} role={user.role} />
      )}
      {isField && fieldPresentation.trades && !truckingMode && <FieldMore key={fieldPresentation.identity} open={fieldNavOpen} onClose={() => setFieldNavOpen(false)} user={user} onLogout={onLogout} />}
      {isField && (!fieldPresentation.trades || truckingMode) && (
        <FieldNavDrawer
          open={fieldNavOpen}
          onClose={() => setFieldNavOpen(false)}
          user={user}
          chatUnread={chatUnread}
          onLogout={onLogout}
        />
      )}
      <InstallPrompt />
      </div>
    </div>
  );
}

// Small weather chip in the office top bar — renders only when the browser
// already has geolocation permission and a forecast comes back.
function TopBarWeather() {
  const wx = useTodayWeather();
  if (!wx || wx.temp_max == null) return null;
  return (
    <div className="hidden xl:flex items-center gap-2 pr-1" data-testid="topbar-weather">
      <Sun className="w-4 h-4 text-[#FF5F15]" />
      <div className="leading-tight text-left">
        <div className="text-[12px] font-bold tabular-nums">{Math.round(wx.temp_max)}°C</div>
        <div className="text-[11px] text-zinc-500 dark:text-[#8B94A7]">{WX_TEXT(wx.weather_code)}</div>
      </div>
    </div>
  );
}

// ============================================================
//  FIELD NAV DRAWER — slide-out menu for the field worker app
// ============================================================
function FieldNavItem({ item }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={`field-bottom-${item.label.toLowerCase()}`}
      className={({ isActive }) =>
        `relative flex flex-col items-center justify-center pt-2 pb-1.5 transition-all active:scale-95 ${
          isActive ? "text-[#FF5F15]" : "text-zinc-500 dark:text-zinc-400"
        }`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && <span className="absolute top-0 h-[3px] w-9 rounded-b-full bg-[#FF5F15]" />}
          <Icon className="w-5 h-5" strokeWidth={isActive ? 2.2 : 1.8} />
          <span className={`text-[11px] mt-0.5 ${isActive ? "font-bold" : "font-medium"}`}>{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

function FieldNavDrawer({ open, onClose, user, chatUnread, onLogout }) {
  const workMode = useFieldWorkMode();
  const driverMode = workMode.dual && workMode.mode === "trucking";
  const [stats, setStats] = useState(null);
  const [showcase, setShowcase] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get("/field/drawer-stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/progress/showcase-mini").then((r) => setShowcase(r.data.badges || [])).catch(() => {});
  }, [open]);

  const groups = [
    {
      label: "Overview",
      items: [
        { to: "/app", label: "Dashboard", desc: "Overview & summary", icon: LayoutDashboard, end: true },
        { to: "/app/crew-needs", label: "Crew needs", desc: "Materials to order & yard pickups", icon: ClipboardList },
      ],
    },
    {
      label: "Operations",
      items: [
        { to: "/app/jobs", label: "Jobs", desc: "Manage and track jobs", icon: Briefcase },
        { to: "/app/timesheets", label: "Timesheets", desc: "Track and approve hours", icon: Clock },
        ...(user.truck_driver ? [{ to: "/app/my-runs", label: "My Runs", desc: "My assigned trucking work", icon: Truck }, { to: "/app/running-sheets", label: "Running sheets", desc: "Daily truck load records", icon: Truck }] : []),
        ...(user.contractor ? [{ to: "/app/contractor-invoices", label: "My invoices", desc: "Contractor invoicing", icon: Receipt }] : []),
        { to: "/app/my-safety", label: "My Safety", desc: "My assigned corrective actions", icon: ShieldCheck },
        { to: "/app/safety", label: "Safety", desc: "Safety reports & incidents", icon: ShieldAlert },
        { to: "/app/fuel", label: "Fuel", desc: "Fuel tracking & logs", icon: Fuel },
        { to: "/app/toolbox", label: "Toolbox", desc: "Toolbox talks & forms", icon: ClipboardCheck },
      ],
    },
    {
      label: "Workforce",
      items: [
        { to: "/app/progress", label: "My Progress", desc: "Streaks & achievements", icon: Trophy },
        { to: "/app/chat", label: "Messages", desc: "Team communication", icon: MessageSquare, badge: chatUnread },
        { to: "/app/leave", label: "Leave", desc: "Leave requests & balance", icon: CalendarDays },
        { to: "/app/suppliers", label: "Contacts", desc: "Team & emergency contacts", icon: Phone },
      ],
    },
    {
      label: "Documents",
      items: [
        { to: "/app/sssps", label: "Documents", desc: "Policies, forms & files", icon: FileText },
        { to: "/app/my-company", label: "My Company", desc: "Company policies & H&S", icon: Building2 },
      ],
    },
    {
      label: "System",
      items: [
        { to: "/app/profile", label: "Settings", desc: "App preferences & setup", icon: Settings },
        { to: "/app/support", label: "Help Centre", desc: "Support & resources", icon: HelpCircle },
      ],
    },
  ];

  const roleLabel = { field: "Field worker", admin: "Admin", office: "Office", mechanic: "Mechanic", quantity_surveyor: "Quantity Surveyor" }[user.role] || user.role;
  const quickStats = [
    { icon: Briefcase, value: stats?.active_jobs ?? "—", label: "Active Jobs" },
    { icon: Users, value: stats?.staff_online ?? "—", label: "Staff Online" },
    { icon: TriangleAlert, value: stats?.safety_alerts ?? "—", label: "Safety Alerts" },
  ];

  return (
    <Sheet open={open} onOpenChange={value => { if (!value) onClose(); }}>
      <SheetContent side="left" className="field-drawer w-[88%] max-w-[360px] p-0 flex flex-col gap-0 overflow-hidden" data-testid="field-nav-drawer" onCloseAutoFocus={event => { event.preventDefault(); document.querySelector('[data-testid="field-menu-button"]')?.focus(); }}>
        <SheetTitle className="sr-only">Fieldworker navigation</SheetTitle>
        <SheetDescription className="sr-only">Your work, safety, documents and account.</SheetDescription>
        {/* Hero header — construction image + gradient + logo */}
        <div className="relative shrink-0">
          <img src="/brand/field-hero.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1018]/40 via-[#0B1018]/70 to-[#0B1018]" />
          <div className="relative px-6 pt-7 pb-5">
            <div className="flex items-start justify-between">
              <WorkspaceIdentity dark />

            </div>
          </div>
        </div>

        {/* Glass profile card */}
        <div className="px-4 shrink-0">
          <Link
            to="/app/profile"
            onClick={onClose}
            className="flex items-center gap-3.5 p-3.5 rounded-[20px] bg-white/[0.05] border border-white/10  hover:bg-white/[0.08] transition-colors"
            data-testid="field-drawer-view-profile"
          >
            <Avatar className="w-12 h-12 rounded-full ring-2 ring-[#FF5F15]/40">
              {user.avatar_path ? (
                <AvatarImage src={`${api.defaults.baseURL}/files/view?path=${encodeURIComponent(user.avatar_path)}&auth=${encodeURIComponent(localStorage.getItem("fl_token") || "")}`} alt={user.name} />
              ) : null}
              <AvatarFallback className="bg-[#FF5F15] text-white text-sm font-bold rounded-full">
                {user.name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="font-bold text-[16px] text-[#F8FAFC] truncate">{user.name}</div>
              <div className="text-[12px] text-[#94A3B8] flex items-center gap-1.5">
                {roleLabel} <ShieldCheck className="w-3.5 h-3.5 text-[#FF5F15]" />
              </div>
            </div>
            <ChevronDown className="w-4 h-4 text-[#94A3B8] shrink-0" />
          </Link>
          {showcase.length > 0 && (
            <div className="flex items-center gap-2 mt-2 px-1" data-testid="drawer-showcase-badges">
              {showcase.map((b) => (
                <img key={b.id} src={b.image} alt={b.name} title={b.name} className="w-9 h-9 object-contain" />
              ))}
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8] ml-1">Showcase</span>
            </div>
          )}
        </div>

        {/* Quick stats */}
        <div className="px-4 pt-3 shrink-0">
          <div className="rounded-[20px] bg-[#101620] border border-[#1F2937] grid grid-cols-3 divide-x divide-[#1F2937]" data-testid="field-drawer-stats">
            {quickStats.map((s) => (
              <div key={s.label} className="flex flex-col items-center gap-1 py-3.5 px-1">
                <s.icon className="w-[18px] h-[18px] text-[#FF5F15]" strokeWidth={2} />
                <span className="font-display font-black text-xl leading-none text-[#F8FAFC]">{s.value}</span>
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] text-center leading-tight">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Nav groups */}
        <nav className="flex-1 overflow-y-auto px-4 pt-4 pb-2">
          <FieldWorkModeSwitch onSelected={onClose} />
          {(driverMode ? [{label:"Trucking",items:[{to:"/app/my-runs",label:"My Run",icon:Truck},{to:"/app/trucking-time",label:"My Time",icon:Clock},{to:"/app/running-sheets",label:"Run History",icon:Clock},{to:"/app/prestarts",label:"Pre-start",icon:Wrench},{to:"/app/fuel",label:"Fuel",icon:Fuel},{to:"/app/inbox",label:"Alerts",icon:Bell},{to:"/app/chat",label:"Messages",icon:Bell}]}] : groups).map((g) => (
            <div key={g.label} className="mb-3">
              <div className="px-2 pb-1.5 text-[11px] font-bold uppercase tracking-[0.25em] text-[#94A3B8]/70">{g.label}</div>
              <div className="space-y-1">
                {g.items.map((i) => {
                  const Icon = i.icon;
                  const slug = i.label.toLowerCase().replace(/\s+/g, "-");
                  return (
                    <NavLink
                      key={i.to + i.label}
                      to={i.to}
                      end={i.end}
                      onClick={onClose}
                      data-testid={`field-drawer-${slug}`}
                      className={({ isActive }) =>
                        `relative flex items-center gap-3.5 px-3.5 py-2.5 rounded-xl transition-all duration-200 ${
                          isActive
                            ? "bg-[#FF5F15]/[0.13] shadow-[0_0_24px_-6px_rgba(255,95,21,0.45)]"
                            : "hover:bg-white/[0.04]"
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {isActive && <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full bg-[#FF5F15]" />}
                          <Icon className={`w-5 h-5 shrink-0 ${isActive ? "text-[#FF5F15]" : "text-[#94A3B8]"}`} strokeWidth={1.9} />
                          <span className="flex-1 min-w-0 leading-tight">
                            <span className={`block text-[15px] ${isActive ? "font-bold text-[#F8FAFC]" : "font-semibold text-[#E2E8F0]"}`}>{i.label}</span>
                            <span className="block text-[11.5px] text-[#94A3B8] mt-0.5">{i.desc}</span>
                          </span>
                          {i.badge > 0 && (
                            <span className="w-5 h-5 rounded-full bg-[#FF5F15] text-white text-[11px] font-bold flex items-center justify-center shrink-0" data-testid="field-drawer-messages-badge">
                              {i.badge > 9 ? "9+" : i.badge}
                            </span>
                          )}
                        </>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Account — Log out */}
          <div className="mb-3">
            <div className="px-2 pb-1.5 text-[11px] font-bold uppercase tracking-[0.25em] text-[#94A3B8]/70">Account</div>
            <button
              onClick={() => { onClose(); onLogout(); }}
              className="w-full flex items-center gap-3.5 px-3.5 py-2.5 rounded-xl bg-[#FF5F15]/[0.07] border border-[#FF5F15]/20 hover:bg-[#FF5F15]/[0.12] transition-colors text-left"
              data-testid="field-drawer-logout"
            >
              <LogOut className="w-5 h-5 shrink-0 text-[#FF5F15]" strokeWidth={1.9} />
              <span className="flex-1 min-w-0 leading-tight">
                <span className="block text-[15px] font-bold text-[#FF5F15]">Log Out</span>
                <span className="block text-[11.5px] text-[#94A3B8] mt-0.5">Sign out of your account</span>
              </span>
            </button>
          </div>
          <div style={{ height: "max(env(safe-area-inset-bottom, 0px), 16px)" }} />
        </nav>
      </SheetContent>
    </Sheet>
  );
}

// ============================================================
//  COCKPIT ICON RAIL — desktop office shell (lg+)
// ============================================================

// A single icon link on the rail (for ungrouped items like Overview/Inbox/Chat).
// When `expanded` is true, shows label next to icon and stretches full width.
// Dark branded sidebar (both themes) — icons default to a warm orange tint,
// active rows use the Fieldlink orange pill.
const navClr = () => "fl-nav-icon text-[#FF8B4F]";
// Small "NEW" feature chip used across nav variants.
const NewChip = () => (
  <span className="px-1.5 py-px rounded bg-[#FF5F15] text-white text-[11px] font-bold uppercase tracking-wider" data-testid="nav-new-badge">New</span>
);

const ROW_ACTIVE = "fl-nav-active bg-[#FF5F15] text-[#07111A] font-semibold [&_svg]:text-[#07111A]";
const ROW_IDLE = "fl-nav-idle text-[#C6CEDA] hover:bg-white/[0.05] hover:text-white";
// Nested child active state — subtle: orange left rail + lifted surface.
const CHILD_ACTIVE = "fl-nav-active bg-white/[0.07] text-white font-semibold shadow-[inset_2px_0_0_#FF5F15] [&_svg]:text-[#FF8B4F]";

function RailLink({ to, end, icon: Icon, label, testId, badge, expanded, isNew }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      title={expanded ? undefined : label}
      className={({ isActive }) =>
        `relative my-px rounded-lg flex items-center transition-colors duration-150 ${
          expanded ? "w-full gap-2.5 px-2.5 h-10 text-[13px] font-semibold" : "w-10 h-10 justify-center"
        } ${isActive ? ROW_ACTIVE : ROW_IDLE}`
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={`w-[18px] h-[18px] shrink-0 ${isActive ? "" : navClr(to)}`} strokeWidth={isActive ? 2.1 : 1.9} />
          {expanded && <span className="flex-1 truncate text-left">{label}</span>}
          {expanded && isNew && <NewChip />}
          {badge > 0 && (
            <span className={`text-[11px] font-bold tabular-nums px-1.5 py-px rounded-full bg-[#FF5F15] text-white ${expanded ? "" : "absolute -top-0.5 -right-0.5"}`}>
              {badge > 9 ? "9+" : badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

// A leaf link in the expanded sidebar. `child` = indented submenu entry.
function SideLeaf({ item, isNew, child }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={item.testId}
      className={({ isActive }) =>
        `relative my-px rounded-lg flex items-center gap-2.5 transition-colors duration-150 ${
          child ? "pl-9 pr-2.5 h-10 text-[12.5px] font-medium" : "px-2.5 h-10 text-[13px] font-semibold"
        } ${isActive ? (child ? CHILD_ACTIVE : ROW_ACTIVE) : ROW_IDLE}`
      }
    >
      {({ isActive }) => (
        <>
          {!child && <Icon className={`w-[18px] h-[18px] shrink-0 ${isActive ? "" : navClr(item.to)}`} strokeWidth={isActive ? 2.1 : 1.9} />}
          {child && <span className={`absolute left-[19px] w-1 h-1 rounded-full ${isActive ? "bg-[#FF5F15]" : "bg-white/25"}`} aria-hidden="true" />}
          <span className="flex-1 truncate text-left">{item.label}</span>
          {isNew && <NewChip />}
        </>
      )}
    </NavLink>
  );
}

const subOpenStore = {
  read() { try { return JSON.parse(localStorage.getItem("fl_nav_sub") || "{}"); } catch { return {}; } },
  write(id, v) { try { localStorage.setItem("fl_nav_sub", JSON.stringify({ ...subOpenStore.read(), [id]: v })); } catch { /* ignore */ } },
};

// An expanded-sidebar item: plain link, or a collapsible submenu (Invoices,
// Safety & QA, Plant & Assets, Dispatch, Timesheets, Team, Reports).
function SideItem({ item, pathname, isNew }) {
  const active = !!item.children && item.children.some((c) => pathname === c.to || pathname.startsWith(c.to + "/"));
  const [open, setOpen] = useState(() => {
    if (!item.children) return false;
    const stored = subOpenStore.read()[item.id];
    return stored != null ? stored || active : active;
  });
  useEffect(() => { if (active) setOpen(true); }, [active]);
  if (!item.children) return <SideLeaf item={item} isNew={isNew} />;
  const Icon = item.icon;
  return (
    <div className="w-full">
      <button
        type="button"
        onClick={() => { setOpen((o) => { subOpenStore.write(item.id, !o); return !o; }); }}
        aria-expanded={open}
        data-testid={item.testId}
        className={`w-full relative my-px rounded-lg flex items-center gap-2.5 px-2.5 h-10 text-[13px] font-semibold transition-colors duration-150 ${
          active && !open ? ROW_ACTIVE : ROW_IDLE
        } ${active && open ? "text-white" : ""}`}
      >
        <Icon className={`w-[18px] h-[18px] shrink-0 ${active && !open ? "" : navClr()}`} strokeWidth={active ? 2.1 : 1.9} />
        <span className="flex-1 truncate text-left">{item.label}</span>
        <ChevronDown className={`w-3.5 h-3.5 opacity-70 transition-transform duration-150 ${open ? "" : "-rotate-90"}`} strokeWidth={2.2} />
      </button>
      {open && (
        <div className="relative" data-testid={`${item.testId}-items`}>
          <span className="absolute left-[19.5px] top-1 bottom-1 w-px bg-white/10" aria-hidden="true" />
          {item.children.map((c) => <SideLeaf key={c.to} item={c} child />)}
        </div>
      )}
    </div>
  );
}

// Collapsed rail: one icon per group; clicking opens a flyout with the whole
// group, including submenu children, so users never need to expand the rail.
function RailGroup({ id, icon: Icon, label, items, active, isNewItem }) {
  const [popOpen, setPopOpen] = useState(false);
  const leafClass = ({ isActive }) =>
    `flex items-center gap-2.5 px-4 py-2 text-[13px] font-semibold transition-colors duration-150 ${
      isActive ? "fl-nav-active bg-[#FF5F15]/15 fl-nav-primary text-white shadow-[inset_2px_0_0_#FF5F15] [&_svg]:text-[#FF8B4F]" : "fl-nav-idle fl-nav-primary text-[#C6CEDA] hover:bg-white/[0.05] hover:text-white [&_svg]:text-[#FF8B4F]"
    }`;
  return (
    <Popover open={popOpen} onOpenChange={setPopOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={label}
          aria-label={label}
          data-testid={`rail-group-${id}`}
          className={`relative w-10 h-10 my-0.5 rounded-lg flex items-center justify-center transition-colors duration-150 ${active ? ROW_ACTIVE : ROW_IDLE}`}
        >
          <Icon className={`w-[18px] h-[18px] ${active ? "" : navClr()}`} strokeWidth={active ? 2.1 : 1.9} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={8}
        className="fl-nav-theme fl-nav-flyout w-64 p-0 rounded-lg fl-nav-border border-white/10 bg-[#07111A] fl-nav-primary text-white overflow-hidden shadow-2xl"
      >
        <div className="px-4 py-2.5 border-b fl-nav-border border-white/[0.07] text-[11px] font-bold tracking-[0.16em] uppercase fl-nav-muted text-[#7B8798]">
          {label}
        </div>
        <div className="py-1 max-h-[70vh] overflow-y-auto">
          {items.map((i) => {
            if (!i.children) {
              const ItemIcon = i.icon;
              return (
                <NavLink key={i.to} to={i.to} end={i.end} data-testid={i.testId} onClick={() => setPopOpen(false)} className={leafClass}>
                  <ItemIcon className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                  <span className="flex-1 truncate">{i.label}</span>
                  {isNewItem && isNewItem(i) && <NewChip />}
                </NavLink>
              );
            }
            return (
              <div key={i.id} className="pt-1">
                <div className="px-4 pt-1.5 pb-0.5 text-[11px] font-bold uppercase tracking-[0.14em] fl-nav-muted text-[#66717F]">{i.label}</div>
                {i.children.map((c) => {
                  const CIcon = c.icon;
                  return (
                    <NavLink key={c.to} to={c.to} end={c.end} data-testid={c.testId} onClick={() => setPopOpen(false)} className={leafClass}>
                      <CIcon className="w-4 h-4 shrink-0 opacity-70" strokeWidth={1.8} />
                      <span className="flex-1 truncate">{c.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
